import {BaseMyopComponent} from "./IMyopComponent";
import {BaseMyopMessage} from "../../messages";
import {CleanupHandler, IComponentDefinitionConfig, ISkinConfig} from "../../common";
import {setDebugStyle} from "../componentCore";
import {loaderOptions} from "../hostSDK";

/**
 * Component wrapper for Myop Web Components that are extracted from HTML
 * and run directly on the host document (not in an iframe).
 *
 * These components use the native Web Components API (Custom Elements + Shadow DOM)
 * and communicate via direct method calls and CustomEvents instead of postMessage.
 */
export class MyopExtractedWebComponent extends BaseMyopComponent {
    private ctaCleanup?: () => void;
    private _ctaHandler?: (action: string, payload: unknown) => void;
    private _ctaQueue: Array<{action: string, payload: unknown}> = [];

    constructor(
        componentDefinition: IComponentDefinitionConfig,
        skin: ISkinConfig,
        public container: HTMLElement,
        private customElement: HTMLElement,
        private tagName: string,
        options?: loaderOptions
    ) {
        super(componentDefinition, container, options);
        this.element = customElement;

        // Web components are initiated immediately (no postMessage handshake needed)
        this.setInitiated();
    }

    cleanupInspect: CleanupHandler = () => {};

    inspect() {
        this.cleanupInspect = setDebugStyle(this.id, 'MyopExtractedWebComponent', 0, this.container, this.customElement, this.componentDefinition);
        return this.cleanupInspect;
    }

    hide() {
        this.customElement.style.opacity = '0';
        this.customElement.style.position = 'absolute';
        this.customElement.style.pointerEvents = 'none';
        this.customElement.style.visibility = 'hidden';
        this.customElement.style.height = '0';
        this.customElement.style.width = '0';
    }

    show() {
        this.customElement.style.opacity = '1';
        this.customElement.style.position = 'unset';
        this.customElement.style.pointerEvents = 'all';
        this.customElement.style.visibility = 'visible';
        this.customElement.style.height = '';
        this.customElement.style.width = '';
    }

    /**
     * Initialize the component with data by calling myop_init_interface directly
     */
    initWithData(data: unknown): void {
        if (typeof (this.customElement as any).myop_init_interface === 'function') {
            (this.customElement as any).myop_init_interface(data);
        }
    }

    /**
     * Get current data from the component
     */
    getData(): unknown {
        if (typeof (this.customElement as any).myop_init_interface === 'function') {
            return (this.customElement as any).myop_init_interface();
        }
        return undefined;
    }

    /**
     * Set CTA handler - unified API with iframe components.
     * Called via props.myop_cta_handler = handler
     */
    setCtaHandler(handler: (action: string, payload: unknown) => void): void {
        // Clean up previous listener if exists
        if (this.ctaCleanup) {
            this.ctaCleanup();
        }

        this._ctaHandler = handler;

        // Flush any queued CTAs that fired before handler was set
        if (this._ctaQueue.length > 0) {
            const queue = [...this._ctaQueue];
            this._ctaQueue = [];
            queue.forEach(item => {
                if (!this.markedForDisposed) {
                    handler(item.action, item.payload);
                }
            });
        }

        // Set up event listener
        const listener = (e: Event) => {
            const customEvent = e as CustomEvent;
            if (customEvent.detail?.action) {
                if (this._ctaHandler && !this.markedForDisposed) {
                    this._ctaHandler(customEvent.detail.action, customEvent.detail.payload);
                } else if (!this._ctaHandler) {
                    // Queue if handler not yet set (shouldn't happen after setCtaHandler, but safety)
                    this._ctaQueue.push({
                        action: customEvent.detail.action,
                        payload: customEvent.detail.payload
                    });
                }
            }
        };

        this.customElement.addEventListener('myop-cta', listener);
        this.ctaCleanup = () => {
            this.customElement.removeEventListener('myop-cta', listener);
        };
    }

    /**
     * For compatibility with message-based components.
     * Web components don't use postMessage, so this is a no-op for most messages.
     */
    send = (message: BaseMyopMessage<any>) => {
        // Web components don't use the postMessage system
        // Instead, interact directly via props.myop_init_interface and props.myop_cta_handler
        const cleanupHandler = super.send(message);

        // Log for debugging
        console.debug('[MyopExtractedWebComponent] send() called - web components use direct API instead', message);

        return cleanupHandler;
    }

    dispose = () => {
        if (this.ctaCleanup) {
            this.ctaCleanup();
        }
        super.dispose();
        this.customElement.parentNode?.removeChild(this.customElement);
    }
}
